import { Box, Typography } from '@mui/material';
import PassBuyFooter, { PASS_BUY_FOOTER_HEIGHT } from './PassBuyFooter';
import PassCardHeader, { PASS_HEADER_COLOR, PASS_HEADER_HEIGHT } from './PassCardHeader';
import {
  passCoinIconSrc,
  passGemsIconSrc,
  vipRoomIconsSprite,
} from '../../assets/bazzar';

const CARD_WIDTH = 400;
const CARD_HEIGHT = 264;

/** Figma 8736:25528 — Rectangle 169 shell border */
const passCardOuterGradient = 'linear-gradient(to top, #822AFF 0%, #FF00F2 41.566%)';

const bodyTextSx = {
  color: '#fff',
  fontWeight: 500,
  fontSize: { xs: 14, md: 14 },
  lineHeight: '20px',
  letterSpacing: '0.5px',
  whiteSpace: 'nowrap',
};

function PassBenefitIcon({ src, crop }) {
  const imageSx = crop?.ml != null
    ? {
        width: crop.width,
        height: crop.height,
        maxWidth: 'none',
        display: 'block',
        ml: crop.ml,
        mt: crop.mt,
      }
    : {
        position: 'absolute',
        display: 'block',
        maxWidth: 'none',
        ...(crop || {
          width: '111.75%',
          height: '169.72%',
          left: '-5.33%',
          top: '-34.14%',
        }),
      };

  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        overflow: 'hidden',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      <Box component="img" src={src} alt="" sx={imageSx} />
    </Box>
  );
}

function PassBenefitRow({ icon, label, iconCrop, iconTransform, trailing }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        minWidth: 0,
        width: '100%',
      }}
    >
      <Box sx={iconTransform ? { transform: iconTransform } : undefined}>
        <PassBenefitIcon src={icon} crop={iconCrop} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
        <Typography sx={bodyTextSx}>{label}</Typography>
        {trailing}
      </Box>
    </Box>
  );
}

/** Figma 10933:2330 — HIT / SUPERHIT / BLOCKBUSTER VIP passes */
export default function VipPassCard({ card, onClick }) {
  const headerBackground = card.headerGradient || card.tone || PASS_HEADER_COLOR;
  const headerColorText = card.toneText || '#fff';
  const headerColorBorder = card.toneBorder || '#00acdc';
  const buyLabel = card.actionLabel || `${card.price}`;

  return (
    <Box
      onClick={() => onClick(card)}
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: { xs: CARD_WIDTH, md: CARD_WIDTH },
        aspectRatio: `${CARD_WIDTH} / ${CARD_HEIGHT}`,
        mx: 'auto',
        cursor: 'pointer',
        borderRadius: '22px',
        background: passCardOuterGradient,
        boxShadow: '0 8px 18px rgba(0, 0, 0, 0.4)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: '2px',
          borderRadius: '20px',
          overflow: 'hidden',
        }}
      >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: `${(PASS_HEADER_HEIGHT / CARD_HEIGHT) * 100}%`,
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          zIndex: 0,
        }}
      >
        <PassCardHeader background={headerBackground} sx={{ height: '100%' }} />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: '100%',
          minWidth: 0,
          height: `${(60 / CARD_HEIGHT) * 100}%`,
          bgcolor: 'rgba(0, 0, 0, 0.2)',
          border: `0.5px solid ${headerColorBorder}`,
          borderBottomLeftRadius: '16px',
          borderBottomRightRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 1,
          zIndex: 1,
        }}
      >
        <Typography
          sx={{
            color: headerColorText,
            fontWeight: 600,
            fontSize: { xs: 16, md: 16 },
            lineHeight: '24px',
            letterSpacing: '0.8px',
            textShadow: '0 1px 0 #fff',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {card.title}
        </Typography>
        <Typography
          sx={{
            color: '#fff',
            fontWeight: 400,
            fontSize: { xs: 10, md: 10 },
            lineHeight: '12px',
            letterSpacing: 0,
            mt: 0.35,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {card.validity}
        </Typography>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: `${(85 / CARD_HEIGHT) * 100}%`,
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '14px',
          px: 2,
          zIndex: 1,
        }}
      >
        {card.points ? <PassBenefitRow icon={passCoinIconSrc} label={card.points} /> : null}

        {card.gemsLabel || card.gemsDetail ? (
          <PassBenefitRow
            icon={passGemsIconSrc}
            iconCrop={{
              width: '97.84%',
              height: '96.77%',
              left: '0.26%',
              top: '1.61%',
            }}
            label={card.gemsLabel || 'RapGems'}
            trailing={
              card.gemsDetail ? (
                <Typography
                  sx={{
                    color: '#fff',
                    fontWeight: 500,
                    fontSize: 11,
                    lineHeight: '16px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {card.gemsDetail}
                </Typography>
              ) : null
            }
          />
        ) : null}

        {card.roomAccess ? (
          <PassBenefitRow
            icon={vipRoomIconsSprite}
            iconCrop={{
              width: '384%',
              height: '250.98%',
              ml: '-23.25%',
              mt: '-75.25%',
            }}
            label={card.roomAccess}
          />
        ) : null}
      </Box>

      <PassBuyFooter
        onClick={(event) => {
          event.stopPropagation();
          onClick(card);
        }}
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: `${(PASS_BUY_FOOTER_HEIGHT / CARD_HEIGHT) * 100}%`,
          minHeight: 'unset',
          zIndex: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography
            sx={{
              color: '#fff',
              fontWeight: 500,
              fontSize: { xs: 18, md: 18 },
              lineHeight: '22px',
              letterSpacing: 0,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            {buyLabel}
          </Typography>
          {card.originalPrice ? (
            <Typography
              sx={{
                color: '#fff',
                fontWeight: 400,
                fontSize: { xs: 14, md: 14 },
                lineHeight: 'normal',
                letterSpacing: '0.8px',
                opacity: 0.7,
                textDecoration: 'line-through',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                mt: 0.25,
              }}
            >
              {card.originalPrice}
            </Typography>
          ) : null}
        </Box>
      </PassBuyFooter>
      </Box>
    </Box>
  );
}
